import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Select from "../../../components/common/Select";
import {
  setManualRateWithChargesThunk,
  fetchFromAPIThunk,
} from "../../../store/thunks/rateThunks";
import {
  CurrencyRupeeIcon,
  RefreshIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
} from "@heroicons/react/solid";

const VerifyRatesStep = ({ onComplete }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [fetchingRates, setFetchingRates] = useState(true);
  const [marketRates, setMarketRates] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [rates, setRates] = useState({
    GOLD: {
      baseRate: 0,
      sellMargin: 2,
      buyMargin: 2,
      makingCharges: { type: "PERCENTAGE", value: 10 },
      gst: { rate: 3, includesMaking: true },
    },
    SILVER: {
      baseRate: 0,
      sellMargin: 3,
      buyMargin: 3,
      makingCharges: { type: "PER_GRAM", value: 5 },
      gst: { rate: 3, includesMaking: true },
    },
    PLATINUM: {
      baseRate: 0,
      sellMargin: 2.5,
      buyMargin: 2.5,
      makingCharges: { type: "PERCENTAGE", value: 12 },
      gst: { rate: 3, includesMaking: true },
    },
    DIAMOND: {
      baseRate: 0,
      sellMargin: 5,
      buyMargin: 5,
      makingCharges: { type: "PERCENTAGE", value: 15 },
      gst: { rate: 3, includesMaking: false },
    },
  });

  const [selectedMetal, setSelectedMetal] = useState("GOLD");
  const [errors, setErrors] = useState({});

  const metalOptions = [
    { value: "GOLD", label: "Gold (22K)" },
    { value: "SILVER", label: "Silver" },
    { value: "PLATINUM", label: "Platinum" },
    { value: "DIAMOND", label: "Diamond" },
  ];

  const makingChargeTypes = [
    { value: "PERCENTAGE", label: "Percentage (%)" },
    { value: "PER_GRAM", label: "Per Gram (₹)" },
    { value: "FIXED", label: "Fixed Amount (₹)" },
  ];

  // Fetch market rates on component mount
  useEffect(() => {
    fetchMarketRates();
  }, []);

  const fetchMarketRates = async () => {
    setFetchingRates(true);
    try {
      // Fetch market rates from API
      const response = await dispatch(fetchFromAPIThunk()).unwrap();

      if (response.data) {
        const apiRates = response.data;
        setMarketRates(apiRates);
        setLastFetchTime(new Date());

        // Update rates with fetched market rates
        setRates((prevRates) => ({
          GOLD: {
            ...prevRates.GOLD,
            baseRate: apiRates.GOLD?.baseRate || apiRates.GOLD?.rate || 6500,
          },
          SILVER: {
            ...prevRates.SILVER,
            baseRate: apiRates.SILVER?.baseRate || apiRates.SILVER?.rate || 75,
          },
          PLATINUM: {
            ...prevRates.PLATINUM,
            baseRate:
              apiRates.PLATINUM?.baseRate || apiRates.PLATINUM?.rate || 3500,
          },
          DIAMOND: {
            ...prevRates.DIAMOND,
            baseRate:
              apiRates.DIAMOND?.baseRate || apiRates.DIAMOND?.rate || 25000,
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch market rates:", error);
      // Use default rates if fetch fails
      setRates((prevRates) => ({
        ...prevRates,
        GOLD: { ...prevRates.GOLD, baseRate: 6500 },
        SILVER: { ...prevRates.SILVER, baseRate: 75 },
        PLATINUM: { ...prevRates.PLATINUM, baseRate: 3500 },
        DIAMOND: { ...prevRates.DIAMOND, baseRate: 25000 },
      }));
    } finally {
      setFetchingRates(false);
    }
  };

  const handleRefreshRates = () => {
    fetchMarketRates();
  };

  const handleRateChange = (metal, field, value) => {
    setRates({
      ...rates,
      [metal]: {
        ...rates[metal],
        [field]: value,
      },
    });
  };

  const handleMakingChargeChange = (metal, field, value) => {
    setRates({
      ...rates,
      [metal]: {
        ...rates[metal],
        makingCharges: {
          ...rates[metal].makingCharges,
          [field]: value,
        },
      },
    });
  };

  const calculateSellRate = (baseRate, margin) => {
    return baseRate * (1 + margin / 100);
  };

  const calculateBuyRate = (baseRate, margin) => {
    return baseRate * (1 - margin / 100);
  };

  const getDeviationFromMarket = (metal) => {
    if (!marketRates || !marketRates[metal]) return null;
    const marketRate = marketRates[metal].baseRate || marketRates[metal].rate;
    const currentRate = rates[metal].baseRate;
    const deviation = ((currentRate - marketRate) / marketRate) * 100;
    return deviation;
  };

  const handleSaveRates = async () => {
    setLoading(true);
    try {
      const promises = Object.entries(rates).map(([type, rateData]) => {
        const payload = {
          type,
          baseRate: parseFloat(rateData.baseRate),
          sellRate: calculateSellRate(rateData.baseRate, rateData.sellMargin),
          buyRate: calculateBuyRate(rateData.baseRate, rateData.buyMargin),
          currency: "INR",
          unit: "GM",
          charges: {
            making: rateData.makingCharges,
            gst: rateData.gst,
            hallmark: {
              enabled: true,
              charge: 50,
            },
          },
        };

        return dispatch(setManualRateWithChargesThunk(payload)).unwrap();
      });

      await Promise.all(promises);

      onComplete({
        ratesConfigured: true,
        rates: rates,
        marketRates: marketRates,
      });
    } catch (error) {
      setErrors({
        general: error || "Failed to save rates",
      });
    } finally {
      setLoading(false);
    }
  };

  const currentRate = rates[selectedMetal];
  const deviation = getDeviationFromMarket(selectedMetal);

  if (fetchingRates) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Fetching current market rates...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Configure Base Rates</h3>
        <p className="text-gray-600">
          We've fetched the current market rates. You can adjust margins and
          charges as needed.
        </p>

        {/* Market Rate Status */}
        {marketRates && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Market rates loaded successfully
                </p>
                {lastFetchTime && (
                  <p className="text-xs text-blue-600 mt-1">
                    Last updated: {lastFetchTime.toLocaleTimeString()}
                  </p>
                )}
              </div>
              <Button
                variant="secondary-outline"
                size="sm"
                onClick={handleRefreshRates}
                icon={<RefreshIcon className="w-4 h-4" />}
              >
                Refresh Rates
              </Button>
            </div>
          </div>
        )}
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errors.general}
        </div>
      )}

      <div className="mb-6">
        <Select
          label="Select Metal Type"
          value={selectedMetal}
          onChange={(e) => setSelectedMetal(e.target.value)}
          options={metalOptions}
        />
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Base Rate (₹/gram)
              </label>
              {deviation !== null && (
                <span
                  className={`text-xs flex items-center ${
                    Math.abs(deviation) < 2
                      ? "text-green-600"
                      : Math.abs(deviation) < 5
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  {deviation > 0 ? (
                    <ArrowUpIcon className="w-3 h-3 mr-1" />
                  ) : (
                    <ArrowDownIcon className="w-3 h-3 mr-1" />
                  )}
                  {Math.abs(deviation).toFixed(2)}% from market
                </span>
              )}
            </div>
            <Input
              type="number"
              value={currentRate.baseRate}
              onChange={(e) =>
                handleRateChange(selectedMetal, "baseRate", e.target.value)
              }
              min="0"
              step="0.01"
            />
            {marketRates && marketRates[selectedMetal] && (
              <p className="text-xs text-gray-500 mt-1">
                Market rate: ₹
                {marketRates[selectedMetal].baseRate ||
                  marketRates[selectedMetal].rate}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex space-x-4">
              <Input
                label="Sell Margin (%)"
                type="number"
                value={currentRate.sellMargin}
                onChange={(e) =>
                  handleRateChange(selectedMetal, "sellMargin", e.target.value)
                }
                min="0"
                max="100"
                step="0.1"
              />
              <Input
                label="Buy Margin (%)"
                type="number"
                value={currentRate.buyMargin}
                onChange={(e) =>
                  handleRateChange(selectedMetal, "buyMargin", e.target.value)
                }
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-medium mb-3">Making Charges</h4>
            <div className="flex space-x-4">
              <Select
                label="Charge Type"
                value={currentRate.makingCharges.type}
                onChange={(e) =>
                  handleMakingChargeChange(
                    selectedMetal,
                    "type",
                    e.target.value
                  )
                }
                options={makingChargeTypes}
              />
              <Input
                label={
                  currentRate.makingCharges.type === "PERCENTAGE"
                    ? "Value (%)"
                    : "Value (₹)"
                }
                type="number"
                value={currentRate.makingCharges.value}
                onChange={(e) =>
                  handleMakingChargeChange(
                    selectedMetal,
                    "value",
                    e.target.value
                  )
                }
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-medium mb-3">Rate Preview</h4>
            <div className="bg-white rounded p-4 grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Base Rate</p>
                <p className="text-lg font-semibold">₹{currentRate.baseRate}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Selling Rate</p>
                <p className="text-lg font-semibold text-green-600">
                  ₹
                  {calculateSellRate(
                    currentRate.baseRate,
                    currentRate.sellMargin
                  ).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Buying Rate</p>
                <p className="text-lg font-semibold text-blue-600">
                  ₹
                  {calculateBuyRate(
                    currentRate.baseRate,
                    currentRate.buyMargin
                  ).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Summary of All Rates */}
          <div className="md:col-span-2 mt-4">
            <h4 className="font-medium mb-3">All Metal Rates Summary</h4>
            <div className="bg-white rounded p-4 space-y-2">
              {Object.entries(rates).map(([metal, data]) => (
                <div
                  key={metal}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <span className="font-medium">{metal}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm">Base: ₹{data.baseRate}</span>
                    <span className="text-sm text-green-600">
                      Sell: ₹
                      {calculateSellRate(
                        data.baseRate,
                        data.sellMargin
                      ).toFixed(2)}
                    </span>
                    <span className="text-sm text-blue-600">
                      Buy: ₹
                      {calculateBuyRate(data.baseRate, data.buyMargin).toFixed(
                        2
                      )}
                    </span>
                    {marketRates && marketRates[metal] && (
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <Button
          variant="secondary-outline"
          onClick={() => onComplete({ skipped: true })}
        >
          Skip & Use Defaults
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveRates}
          isLoading={loading}
          disabled={loading}
        >
          Save Rates & Continue
        </Button>
      </div>
    </div>
  );
};

export default VerifyRatesStep;
